import { useLocation, useNavigate } from "react-router-dom";
import "./Tickets.scss";
import { useEffect, useState } from "react";
import { FiSearch } from "react-icons/fi";
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
    TablePagination
} from "@mui/material";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import newRequest from "../../../utils/newRequest.js";
import { ScaleLoader } from "react-spinners";
import { CustomLoadingButton } from "../../../components/ui/CustomButton.jsx";
import ConfirmDialog from "../../../components/confirmDialog/confirmDialog.jsx";
import { MdAdd, MdArrowBackIos, MdClose } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { logData } from "../../../utils/genericFunctions.js";

const Tickets = () => {
    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [confirmDialogAction, setConfirmDialogAction] = useState("None");
    const [confirmationActiontext, setConfirmationActiontext] = useState("");
    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");
    const [searchTerm, setSearchTerm] = useState("");
    const [tickets, setTickets] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(4);
    const [activeTab, setActiveTab] = useState("ALL"); // ALL | OPEN | IN_PROGRESS | RESOLVED
    const showBack = location.state?.showBack || "N";

    const [serverRows, setServerRows] = useState([]);
    const [serverTotal, setServerTotal] = useState(0);
    const [serverMode, setServerMode] = useState(false);
    const [slaNowTick, setSlaNowTick] = useState(Date.now());


    const handleConfirm = async () => {
        try {

        } catch (err) {
            console.log(err);
        }
        setIsConfirmDialogOpen(false);
    };


    const fetchTickets = async () => {
        try {
            setIsLoading(true);
            const res = await newRequest.post(
                "/ticket/findMyTickets",
                {
                    tk_status: "",
                    tk_priority: "",
                    tk_type: "",
                    from_date: "",
                    to_date: ""
                }
            );
            setTickets(res.data);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setIsLoading(false);
        }
    };


    const getInitials = (name = "") => {
        const parts = String(name).trim().split(/\s+/).filter(Boolean);
        if (!parts.length) return "U";
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[1][0]).toUpperCase();
    };

    const timeAgo = (iso) => {
        if (!iso) return "";
        const t = new Date(iso).getTime();
        if (!t) return "";
        const now = Date.now();
        const diff = Math.max(0, now - t);

        const mins = Math.floor(diff / 60000);
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 7) return `${days}d ago`;
        const wks = Math.floor(days / 7);
        return `${wks}w ago`;
    };

    const getPriorityMeta = (p) => {
        const v = String(p || "").toLowerCase();
        if (v === "critical") return { label: "CRITICAL", cls: "priority--critical" };
        if (v === "urgent") return { label: "HIGH PRIORITY", cls: "priority--urgent" };
        if (v === "high") return { label: "HIGH", cls: "priority--high" };
        if (v === "normal") return { label: "MEDIUM", cls: "priority--normal" };
        if (v === "low") return { label: "LOW", cls: "priority--low" };
        return { label: String(p || "MEDIUM").toUpperCase(), cls: "priority--normal" };
    };

    const getStatusMeta = (s) => {
        const v = String(s || "");
        if (v === "In Progress") return { label: "In Progress", cls: "status--inprogress", dot: "dot--blue" };
        if (v === "Open") return { label: "Open", cls: "status--open", dot: "dot--blue" };
        if (v === "New") return { label: "Pending", cls: "status--pending", dot: "dot--gray" };
        if (v === "Waiting on Customer" || v === "Waiting on Vendor")
            return { label: "Pending", cls: "status--pending", dot: "dot--gray" };
        if (v === "Resolved") return { label: "Resolved", cls: "status--resolved", dot: "dot--green" };
        if (v === "Closed") return { label: "Resolved", cls: "status--resolved", dot: "dot--green" };
        return { label: v || "Pending", cls: "status--pending", dot: "dot--gray" };
    };

    const splitDuration = (totalSeconds = 0) => {
        const s = Math.max(0, Math.floor(totalSeconds));
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        return { d, h, m, s: sec };
    };

    const fmtRemaining = ({ d, h, m, s }, showSeconds = true) => {
        const dd = String(d).padStart(2, "0");
        const hh = String(h).padStart(2, "0");
        const mm = String(m).padStart(2, "0");
        const ss = String(s).padStart(2, "0");
        if (d > 0) return showSeconds ? `${dd}d ${hh}h ${mm}m ${ss}s` : `${dd}d ${hh}h ${mm}m`;
        return showSeconds ? `${hh}h ${mm}m ${ss}s` : `${hh}h ${mm}m`;
    };

    // SLA calculation per ticket (uses tk_sla.* from list results)
    const getSlaMeta = (ticket, nowMs) => {
        const sla = ticket?.tk_sla || {};
        const enabled = String(sla?.sla_enabled || "N") === "Y";

        if (!enabled) {
            return {
                enabled: false,
                remainingLabel: "—",
                pct: 0,
                cls: "sla--met",
                breached: false,
                approaching: false,
            };
        }

        const targetMins = Number(sla?.resolution_target_mins || 0);
        const targetSecs = Math.max(0, Math.round(targetMins * 60));

        const startedAtIso = sla?.started_at || ticket?.createdAt;
        const startedMs = startedAtIso ? new Date(startedAtIso).getTime() : null;

        const elapsedSecs = startedMs ? Math.max(0, Math.floor((nowMs - startedMs) / 1000)) : 0;
        const remainingSecs = targetSecs > 0 ? targetSecs - elapsedSecs : 0;

        const breached =
            String(sla?.resolution_breached || "N") === "Y" ||
            (targetSecs > 0 && remainingSecs <= 0);

        const approaching =
            !breached && targetSecs > 0
                ? remainingSecs > 0 && remainingSecs <= Math.ceil(targetSecs * 0.25)
                : false;

        const absSecs = Math.abs(remainingSecs);
        const parts = splitDuration(absSecs);

        // pct is ALWAYS the bar fill %
        const pct = breached
            ? 100
            : targetSecs > 0
                ? Math.round((Math.max(0, remainingSecs) / targetSecs) * 100)
                : 0;

        const remainingLabel = breached
            ? `BREACHED ${fmtRemaining(parts, false)}`
            : `REMAINING ${fmtRemaining(parts, true)}`;

        const cls = breached ? "sla--breached" : approaching ? "sla--risk" : "sla--ok";

        return { enabled: true, remainingLabel, pct, cls, breached, approaching };
    };



    const getUserDisplay = (u) => {
        if (!u) return { name: "", photo: "" };
        if (typeof u === "object") return { name: u?.usr_full_name || "", photo: u?.usr_photo_url || "" };
        return { name: "", photo: "" };
    };

    const Avatar = ({ name, photo, size = 30 }) => {
        const initials = getInitials(name);
        return photo ? (
            <img
                src={photo}
                alt=""
                className="ticket-avatar-img"
                style={{ width: size, height: size }}
            />
        ) : (
            <div className="ticket-avatar-fallback" style={{ width: size, height: size }}>
                {initials}
            </div>
        );
    };

    const searchFilteredTickets = tickets.filter((t) => {
        if (!searchTerm) return true;

        const q = searchTerm.toLowerCase();
        return (
            t?.tk_no?.toLowerCase().includes(q) ||
            t?.tk_subject?.toLowerCase().includes(q) ||
            t?.tk_category?.toLowerCase().includes(q) ||
            t?.tk_subcategory?.toLowerCase().includes(q) ||
            t?.tk_priority?.toLowerCase().includes(q)
        );
    });

    // 2) Tab filter
    const tabFilteredTickets = searchFilteredTickets.filter((t) => {
        if (activeTab === "ALL") return true;

        const st = String(t?.tk_status || "");
        if (activeTab === "OPEN") return st === "Open" || st === "New";
        if (activeTab === "IN_PROGRESS") return st === "In Progress";
        if (activeTab === "RESOLVED") return st === "Resolved";
        return true;
    });

    const effectiveTickets = serverMode ? serverRows : tabFilteredTickets;

    const pagedTickets = serverMode
        ? effectiveTickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : tabFilteredTickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    useEffect(() => {
        const q = searchTerm.trim();

        // Reset server mode when clearing search
        if (!q) {
            setServerMode(false);
            setServerRows([]);
            setServerTotal(0);
            return;
        }

        // Only fallback if local has zero results
        if (tabFilteredTickets.length > 0) {
            setServerMode(false);
            setServerRows([]);
            setServerTotal(0);
            return;
        }

        const timer = setTimeout(async () => {
            try {
                setIsLoading(true);
                const res = await newRequest.post("/ticket/searchMine", {
                    search_input: q,
                    page: 1,
                    result_limit: 50,
                    active_tab: activeTab, // "ALL" | "OPEN" | ...
                });

                setServerMode(true);
                setServerRows(res.data?.rows || []);
                setServerTotal(res.data?.total || 0);
                setPage(0);
            } catch (e) {
                console.log(e);
            } finally {
                setIsLoading(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [searchTerm, activeTab]); // depends on your tab filter

    useEffect(() => {
        // only tick while tickets list is on screen
        const id = setInterval(() => setSlaNowTick(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);



    useEffect(() => {
        fetchTickets();
    }, []);

    return (
        <div>
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <motion.div
                    className="tickets"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="tickets-wrapper">
                        <div className="tickets-center-wrapper">
                            <div className="tickets-center-details">
                                <div className="tickets-navbar">
                                    <div className="navbar-left">
                                        <span className="page-title">{t('tickets')}</span>
                                    </div>
                                    <div className="navbar-right">
                                        <CustomLoadingButton
                                            currentTheme={currentTheme}
                                            iconPosition="end"
                                            borderRadiusType="both"
                                            variant="tint"
                                            color="#0ea5a4"
                                            tooltip={"Add new ticket"}
                                            endIcon={<MdAdd />}
                                            onClick={() => {
                                                try {

                                                } catch (error) {
                                                    console.log(error);
                                                }
                                            }}
                                        >
                                            {t('newTicket')}
                                        </CustomLoadingButton>
                                        {/* This part shows if the menu was opened from home screen */}
                                        {showBack === "Y" && <hr className="separator-v" />}
                                        {showBack === "Y" &&
                                            <span className="back-icon" onClick={() => navigate(-1)}>
                                                <MdArrowBackIos size={24} />
                                            </span>}
                                    </div>
                                </div>
                                <div className="tickets-content">
                                    <div className="tickets-list">
                                        <div className="tickets-list-header">
                                            <div className="homeSearch" style={{ width: "40%" }}>
                                                <FiSearch className="homeSearchIcon" />
                                                <input
                                                    className="homeSearchInput"
                                                    type="text"
                                                    placeholder={t("search")}
                                                    value={searchTerm}
                                                    onChange={(e) => {
                                                        setSearchTerm(e.target.value);
                                                        setPage(0);
                                                    }}
                                                />
                                                {searchTerm?.length > 0 ? (
                                                    <MdClose className="homeSearchClear" onClick={async () => setSearchTerm("")} />
                                                ) : (
                                                    <span className="homeSearchClearPlaceholder" />
                                                )}
                                            </div>

                                            <div className="tickets-toggle">
                                                <button
                                                    type="button"
                                                    className={`tickets-toggle-btn ${activeTab === "ALL" ? "active" : ""}`}
                                                    onClick={() => {
                                                        setActiveTab("ALL");
                                                        setPage(0);
                                                    }}
                                                >
                                                    {t('allTickets')}
                                                </button>

                                                <button
                                                    type="button"
                                                    className={`tickets-toggle-btn ${activeTab === "OPEN" ? "active" : ""}`}
                                                    onClick={() => {
                                                        setActiveTab("OPEN");
                                                        setPage(0);
                                                    }}
                                                >
                                                    {t('open')}
                                                </button>

                                                <button
                                                    type="button"
                                                    className={`tickets-toggle-btn ${activeTab === "IN_PROGRESS" ? "active" : ""}`}
                                                    onClick={() => {
                                                        setActiveTab("IN_PROGRESS");
                                                        setPage(0);
                                                    }}
                                                >
                                                    {t('inProgress')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`tickets-toggle-btn ${activeTab === "RESOLVED" ? "active" : ""}`}
                                                    onClick={() => {
                                                        setActiveTab("RESOLVED");
                                                        setPage(0);
                                                    }}
                                                >
                                                    {t('resolved')}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="tickets-cards">
                                            {pagedTickets.map((tk) => {
                                                const pr = getPriorityMeta(tk?.tk_priority);
                                                const st = getStatusMeta(tk?.tk_status);
                                                const sla = getSlaMeta(tk, slaNowTick);
                                                const owner = getUserDisplay(tk?.tk_owner);
                                                const assignee = getUserDisplay(tk?.tk_assigned_to);

                                                const desc =
                                                    (tk?.tk_description && String(tk.tk_description).trim()) ||
                                                    "—";

                                                const categoryPill = String(tk?.tk_category || "GENERAL").toUpperCase();

                                                return (
                                                    <div className="ticket-card" key={tk?._id}>
                                                        <div className="ticket-card-top">
                                                            <div className="ticket-card-badges">
                                                                <span className="ticket-pill ticket-pill--muted">{tk?.tk_no || ""}</span>
                                                                <span className={`ticket-pill ticket-pill--priority ${pr.cls}`}>{pr.label}</span>
                                                                <span className="ticket-pill ticket-pill--tag">{categoryPill}</span>
                                                            </div>

                                                            <div className={`ticket-status ${st.cls}`}>
                                                                <span className={`ticket-status-dot ${st.dot}`} />
                                                                <span className="ticket-status-text">{st.label}</span>
                                                            </div>
                                                        </div>

                                                        <div className="ticket-card-body">
                                                            <div className="ticket-main">
                                                                <div className="ticket-title">{tk?.tk_subject || ""}</div>
                                                                <div className="ticket-desc">{desc}</div>

                                                                <div className="ticket-meta">
                                                                    <div className="ticket-meta-item">
                                                                        <Avatar name={owner?.name} photo={owner?.photo} size={28} />
                                                                        <span className="ticket-meta-label">
                                                                            {t('reportedBy')}:{" "}
                                                                            <span className="ticket-meta-strong">
                                                                                {owner?.name ? owner.name : "Unassigned"}
                                                                            </span>
                                                                        </span>
                                                                    </div>

                                                                    <div className="ticket-meta-item ticket-meta-item--time">
                                                                        <span className="ticket-clock" />
                                                                        <span className="ticket-meta-label">
                                                                            {t('created')} {timeAgo(tk?.createdAt)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="ticket-side">
                                                                <div className="ticket-sla">
                                                                    <div className="ticket-sla-top">
                                                                        <span className="ticket-sla-label">{t('slaStatus')?.toUpperCase()}</span>
                                                                        <motion.span
                                                                            // key={sla.enabled && !sla.breached ? `sla-${tk?._id}-${slaNowTick}` : `sla-static-${tk?._id}`}
                                                                            className={`ticket-sla-remaining ${sla.cls}`}
                                                                            initial={sla.enabled && !sla.breached ? { scale: 0.96, opacity: 0.7 } : false}
                                                                            animate={sla.enabled && !sla.breached ? { scale: 1, opacity: 1 } : false}
                                                                            transition={{ duration: 0.12 }}
                                                                            title={sla.enabled ? "Resolution SLA" : "SLA not enabled"}
                                                                        >
                                                                            {sla.remainingLabel}
                                                                        </motion.span>
                                                                    </div>

                                                                    <div className="ticket-sla-bar">
                                                                        <div
                                                                            className={`ticket-sla-fill ${sla.cls}`}
                                                                            style={{ width: `${Math.min(100, Math.max(0, sla.pct || 0))}%` }}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <button
                                                                    className="ticket-link"
                                                                    onClick={() => navigate(`/tickets/${tk?._id}`)}
                                                                >
                                                                    {st.label === "Resolved" ? "History" : "Details"}
                                                                    <span className="ticket-link-arrow">›</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {pagedTickets.length === 0 && (
                                                <div className="tickets-empty">
                                                    <div className="tickets-empty-title">{t('noTicketsFound')}</div>
                                                    <div className="tickets-empty-sub">Try adjusting your filters.</div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="tickets-pagination">
                                            <div className="tickets-pagination-left">
                                                <span className="tickets-pagination-text">
                                                    {t('showing')}{" "}
                                                    <b>
                                                        {tabFilteredTickets.length === 0
                                                            ? 0
                                                            : page * rowsPerPage + 1}
                                                    </b>
                                                    {" - "}
                                                    <b>{Math.min((page + 1) * rowsPerPage, tabFilteredTickets.length)}
                                                    </b>{" "}
                                                    of <b>{tabFilteredTickets.length}</b> {t('tickets')}
                                                </span>
                                            </div>
                                            <TablePagination
                                                component="div"
                                                count={serverMode ? serverTotal : tabFilteredTickets.length}
                                                page={page}
                                                onPageChange={(e, newPage) => setPage(newPage)}
                                                rowsPerPage={rowsPerPage}
                                                onRowsPerPageChange={(e) => {
                                                    setRowsPerPage(parseInt(e.target.value, 4));
                                                    setPage(0);
                                                }}
                                                rowsPerPageOptions={[4]}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tickets-right-wrapper">
                            stats content
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Dialog to notify user about changes being saved */}
            <Dialog open={isNotifyDialogOpen}
                onClose={() => setIsNotifyDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                        color: currentTheme === "dark" ? "#fff" : "#000",
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                    },
                }}>
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: notifyDialogText }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton currentTheme={currentTheme} variant="tint" color="#E040FB" onClick={() => setIsNotifyDialogOpen(false)}>{t('close')}</CustomLoadingButton>
                </DialogActions>
            </Dialog>
            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                onClose={() => setIsConfirmDialogOpen(false)}
                onConfirm={handleConfirm}
                dialogTitle={t('confirmation')}
                dialogContent={<p dangerouslySetInnerHTML={{ __html: confirmationActiontext }} />}
            />
        </div>
    )
}

export default Tickets;